import { useEffect, useRef } from "react";

// Since we don't have OGL installed, I'll create a fallback WebGL implementation
// that provides similar particle effects

const defaultColors = ["#ff00ff", "#00ffff", "#ffff00"];

const hexToRgb = (hex) => {
  hex = hex.replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex.split("").map((c) => c + c).join("");
  }
  const int = parseInt(hex, 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  return [r, g, b];
};

const vertex = `
  attribute vec3 position;
  attribute vec4 random;
  attribute vec3 color;
  
  uniform mat4 projectionMatrix;
  uniform mat4 viewMatrix;
  uniform float uTime;
  uniform float uSpread;
  uniform float uBaseSize;
  uniform float uSizeRandomness;
  uniform vec2 uMouse;
  uniform float uMouseInfluence;
  
  varying vec4 vRandom;
  varying vec3 vColor;
  
  void main() {
    vRandom = random;
    vColor = color;
    
    vec3 pos = position * uSpread;
    
    float t = uTime * 0.5;
    pos.x += sin(t * random.z + 6.28 * random.w) * mix(0.5, 2.0, random.x);
    pos.y += sin(t * random.y + 6.28 * random.x) * mix(0.5, 2.0, random.w);
    pos.z += sin(t * random.w + 6.28 * random.y) * mix(0.5, 2.0, random.z);
    
    // Mouse influence
    vec2 mouseEffect = uMouse * uMouseInfluence;
    pos.x += mouseEffect.x * (random.x - 0.5) * 2.0;
    pos.y += mouseEffect.y * (random.y - 0.5) * 2.0;
    
    vec4 mvPosition = viewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    
    float size = uBaseSize * (1.0 + uSizeRandomness * (random.x - 0.5));
    gl_PointSize = size * (1.0 / length(mvPosition.xyz));
  }
`;

const fragment = `
  precision highp float;
  
  uniform float uTime;
  uniform float uAlphaParticles;
  varying vec4 vRandom;
  varying vec3 vColor;
  
  void main() {
    vec2 uv = gl_PointCoord.xy;
    float d = length(uv - vec2(0.5));
    
    if(d > 0.5) discard;
    
    float circle = smoothstep(0.5, 0.3, d);
    vec3 finalColor = vColor + 0.3 * sin(uv.yxx + uTime + vRandom.y * 6.28);
    
    float alpha = circle * (uAlphaParticles > 0.5 ? 0.8 : 1.0);
    gl_FragColor = vec4(finalColor, alpha);
  }
`;

const Particles = ({
  particleCount = 200,
  particleSpread = 8,
  speed = 0.1,
  particleColors = defaultColors,
  moveParticlesOnHover = false,
  particleHoverFactor = 1,
  alphaParticles = false,
  particleBaseSize = 100,
  sizeRandomness = 1,
  cameraDistance = 15,
  disableRotation = false,
  className = "",
}) => {
  const containerRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const glRef = useRef(null);
  const programRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create canvas and WebGL context
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) {
      console.warn('WebGL not supported, falling back to canvas particles');
      return;
    }

    glRef.current = gl;
    container.appendChild(canvas);

    // Setup WebGL
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0, 0, 0, 0);

    const resize = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      canvas.width = width;
      canvas.height = height;
      gl.viewport(0, 0, width, height);
    };

    const handleMouseMove = (e) => {
      if (moveParticlesOnHover) {
        const rect = container.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
        mouseRef.current = { x, y };
      }
    };

    // Shader compilation
    const createShader = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('Shader compile error:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vertexShader = createShader(gl.VERTEX_SHADER, vertex);
    const fragmentShader = createShader(gl.FRAGMENT_SHADER, fragment);

    if (!vertexShader || !fragmentShader) return;

    // Create program
    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(program));
      return;
    }

    programRef.current = program;

    // Get uniform locations
    const uniforms = {
      projectionMatrix: gl.getUniformLocation(program, 'projectionMatrix'),
      viewMatrix: gl.getUniformLocation(program, 'viewMatrix'),
      uTime: gl.getUniformLocation(program, 'uTime'),
      uSpread: gl.getUniformLocation(program, 'uSpread'),
      uBaseSize: gl.getUniformLocation(program, 'uBaseSize'),
      uSizeRandomness: gl.getUniformLocation(program, 'uSizeRandomness'),
      uAlphaParticles: gl.getUniformLocation(program, 'uAlphaParticles'),
      uMouse: gl.getUniformLocation(program, 'uMouse'),
      uMouseInfluence: gl.getUniformLocation(program, 'uMouseInfluence'),
    };

    // Create particle data
    const count = particleCount;
    const positions = new Float32Array(count * 3);
    const randoms = new Float32Array(count * 4);
    const colors = new Float32Array(count * 3);
    const palette = particleColors && particleColors.length > 0 ? particleColors : defaultColors;

    for (let i = 0; i < count; i++) {
      // Sphere distribution
      let x, y, z, len;
      do {
        x = Math.random() * 2 - 1;
        y = Math.random() * 2 - 1;
        z = Math.random() * 2 - 1;
        len = x * x + y * y + z * z;
      } while (len > 1 || len === 0);
      
      const r = Math.cbrt(Math.random());
      positions.set([x * r, y * r, z * r], i * 3);
      randoms.set([Math.random(), Math.random(), Math.random(), Math.random()], i * 4);
      
      const col = hexToRgb(palette[Math.floor(Math.random() * palette.length)]);
      colors.set(col, i * 3);
    }

    // Create buffers
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    const randomBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, randomBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, randoms, gl.STATIC_DRAW);

    const colorBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, colors, gl.STATIC_DRAW);

    // Animation loop
    let lastTime = performance.now();
    let elapsed = 0;

    const update = (currentTime) => {
      animationRef.current = requestAnimationFrame(update);
      
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;
      elapsed += deltaTime * speed;

      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);

      // Set uniforms
      const aspect = canvas.width / canvas.height;
      const fov = 15 * Math.PI / 180;
      const near = 0.1;
      const far = 100;
      
      // Projection matrix
      const f = Math.tan(Math.PI * 0.5 - 0.5 * fov);
      const rangeInv = 1.0 / (near - far);
      const projectionMatrix = [
        f / aspect, 0, 0, 0,
        0, f, 0, 0,
        0, 0, (near + far) * rangeInv, -1,
        0, 0, near * far * rangeInv * 2, 0
      ];

      // View matrix (camera at distance)
      const viewMatrix = [
        1, 0, 0, 0,
        0, 1, 0, 0,
        0, 0, 1, 0,
        0, 0, -cameraDistance, 1
      ];

      gl.uniformMatrix4fv(uniforms.projectionMatrix, false, projectionMatrix);
      gl.uniformMatrix4fv(uniforms.viewMatrix, false, viewMatrix);
      gl.uniform1f(uniforms.uTime, elapsed * 0.001);
      gl.uniform1f(uniforms.uSpread, particleSpread);
      gl.uniform1f(uniforms.uBaseSize, particleBaseSize);
      gl.uniform1f(uniforms.uSizeRandomness, sizeRandomness);
      gl.uniform1f(uniforms.uAlphaParticles, alphaParticles ? 1 : 0);
      gl.uniform2f(uniforms.uMouse, mouseRef.current.x, mouseRef.current.y);
      gl.uniform1f(uniforms.uMouseInfluence, particleHoverFactor);

      // Bind attributes
      const positionAttribute = gl.getAttribLocation(program, 'position');
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.enableVertexAttribArray(positionAttribute);
      gl.vertexAttribPointer(positionAttribute, 3, gl.FLOAT, false, 0, 0);

      const randomAttribute = gl.getAttribLocation(program, 'random');
      gl.bindBuffer(gl.ARRAY_BUFFER, randomBuffer);
      gl.enableVertexAttribArray(randomAttribute);
      gl.vertexAttribPointer(randomAttribute, 4, gl.FLOAT, false, 0, 0);

      const colorAttribute = gl.getAttribLocation(program, 'color');
      gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
      gl.enableVertexAttribArray(colorAttribute);
      gl.vertexAttribPointer(colorAttribute, 3, gl.FLOAT, false, 0, 0);

      // Draw
      gl.drawArrays(gl.POINTS, 0, count);
    };

    // Event listeners
    window.addEventListener('resize', resize);
    container.addEventListener('mousemove', handleMouseMove);
    
    resize();
    animationRef.current = requestAnimationFrame(update);

    // Cleanup
    return () => {
      window.removeEventListener('resize', resize);
      container.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (container.contains(canvas)) {
        container.removeChild(canvas);
      }
    };
  }, [
    particleCount,
    particleSpread,
    speed,
    particleColors,
    moveParticlesOnHover,
    particleHoverFactor,
    alphaParticles,
    particleBaseSize,
    sizeRandomness,
    cameraDistance,
    disableRotation,
  ]);

  return (
    <div
      ref={containerRef}
      className={`fixed top-0 left-0 w-full h-full pointer-events-none z-[1] ${className}`}
      style={{ background: 'transparent' }}
    />
  );
};

export default Particles;